/**
 * NotificationFormWizard.jsx
 * Formulario de Notificacao em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Tipo, Notificadores, Alarmes, Comando, Descricao (obrigatorio)
 * 2. Configuracoes - Calendario, Prioridade, Sempre (opcional)
 * 3. Revisao
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
 Box, Grid, Typography, Button, FormControlLabel, Checkbox 
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation, useTranslationKeys } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useCatch } from '../../reactHelper';
import fetchOrThrow from '../../common/util/fetchOrThrow';
import { prefixString, unprefixString } from '../../common/util/stringUtils';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import SelectField from '../../common/components/SelectField';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const alarms = useTranslationKeys((it) => it.startsWith('alarm')).map((it) => ({
    key: unprefixString('alarm', it),
    name: t(it),
  }));

  const testNotificators = useCatch(async () => {
    if (!data.notificators) return;
    await Promise.all(data.notificators.split(/[, ]+/).map(async (notificator) => {
      await fetchOrThrow(`/api/notifications/test/${notificator}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    }));
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            value={data?.type ?? ''}
            onChange={(e) => updateData({ type: e.target.value })}
            endpoint="/api/notifications/types"
            keyGetter={(it) => it.type}
            titleGetter={(it) => it?.type ? t(prefixString('event', it.type)) : ''}
            label={`${t('sharedType')} *`}
            helperText={['geofenceEnter', 'geofenceExit'].includes(data.type) ? t('notificationGeofenceLabel') : null}
            error={!!errors?.type}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            multiple
            value={data.notificators ? data.notificators.split(/[, ]+/) : []}
            onChange={(e) => updateData({ notificators: e.target.value.join() })}
            endpoint="/api/notifications/notificators"
            keyGetter={(it) => it.type}
            titleGetter={(it) => it?.type ? t(prefixString('notificator', it.type)) : ''}
            label={`${t('notificationNotificators')} *`}
            error={!!errors?.notificators}
            className="premium-input"
          />
        </Grid>
      </Grid>

      {data.type === 'alarm' && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectField
              fullWidth
              multiple
              value={
                data.attributes && data.attributes.alarms
                  ? data.attributes.alarms.split(/[, ]+/)
                  : []
              }
              onChange={(e) =>
                updateData({
                  attributes: {
                    ...data.attributes,
                    alarms: e.target.value.join(),
                  },
                })
              }
              data={alarms}
              keyGetter={(it) => it.key}
              label={t('sharedAlarms')}
              className="premium-input"
            />
          </Grid>
        </Grid>
      )}

      {data.notificators?.includes('command') && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectField
              fullWidth
              value={data.commandId}
              onChange={(e) => updateData({ commandId: Number(e.target.value) })}
              endpoint="/api/commands"
              titleGetter={(it) => it.description}
              label={`${t('sharedSavedCommand')} *`}
              error={!!errors?.commandId}
              className="premium-input"
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
            label={t('sharedDescription')}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={testNotificators}
          disabled={!data.notificators}
          sx={{
            borderColor: colors.accent,
            color: colors.accent,
            '&:hover': {
              backgroundColor: colors.accentSubtle,
              borderColor: colors.accent,
            },
            '&.Mui-disabled': {
              borderColor: colors.borderDefault,
              color: colors.textMuted,
            },
          }}
        >
          {t('sharedTestNotificators')}
        </Button>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 2: CONFIGURACOES
// ============================================================================

const SettingsStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardSettingsHelp') || 'Configure opcoes adicionais de agendamento e prioridade.'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            value={data.calendarId}
            onChange={(e) => updateData({ calendarId: Number(e.target.value) })}
            endpoint="/api/calendars"
            label={t('sharedCalendar')}
            className="premium-input"
          />
        </Grid>
        {['geofenceEnter', 'geofenceExit'].includes(data.type) && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectField
              fullWidth
              multiple
              value={data.attributes?.geofenceIds ? data.attributes.geofenceIds.split(',') : []}
              onChange={(e) => {
                const geofenceIds = e.target.value.join();
                const attributes = { ...data.attributes };
                if (geofenceIds) {
                  attributes.geofenceIds = geofenceIds;
                } else {
                  delete attributes.geofenceIds;
                }
                updateData({ attributes });
              }}
              endpoint="/api/geofences"
              keyGetter={(it) => String(it.id)}
              label={t('sharedGeofences')}
              className="premium-input"
            />
          </Grid>
        )}
      </Grid>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          p: 2,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!!data.always}
              onChange={(e) => updateData({ always: e.target.checked })}
              size="small"
              sx={{
                color: colors.textSecondary,
                '&.Mui-checked': { color: colors.accent },
              }}
            />
          }
          label={
            <Typography sx={{ color: colors.textPrimary }}>
              {t('notificationAlways')}
            </Typography>
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={data.attributes?.priority || false}
              onChange={(e) =>
                updateData({
                  attributes: {
                    ...data.attributes,
                    priority: e.target.checked,
                  },
                })
              }
              size="small"
              sx={{
                color: colors.textSecondary,
                '&.Mui-checked': { color: colors.accent },
              }}
            />
          }
          label={
            <Typography sx={{ color: colors.textPrimary }}>
              {t('sharedPriority')}
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedType')} value={data.type ? t(prefixString('event', data.type)) : '-'} />
        <InfoRow label={t('notificationNotificators')} value={data.notificators || '-'} />
        <InfoRow label={t('sharedDescription')} value={data.description || '-'} />
        <InfoRow label={t('notificationAlways')} value={data.always ? t('sharedYes') : t('sharedNo')} />
        <InfoRow label={t('sharedPriority')} value={data.attributes?.priority ? t('sharedYes') : t('sharedNo')} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NotificationFormWizard = ({ notification, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardNotificationBasicDescription') || 'Tipo e notificadores',
      icon: <NotificationsIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.type) errors.type = t('sharedRequired');
        if (!data.notificators) errors.notificators = t('sharedRequired');
        if (data.notificators?.includes('command') && !data.commandId) {
          errors.commandId = t('sharedRequired');
        }
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedType'), value: data.type ? t(prefixString('event', data.type)) : null },
        { label: t('notificationNotificators'), value: data.notificators },
      ],
    },
    {
      label: t('sharedExtra'),
      title: t('sharedExtra'),
      description: t('wizardNotificationSettingsDescription') || 'Opcoes adicionais',
      icon: <SettingsIcon sx={{ fontSize: 18 }} />,
      component: SettingsStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = notification?.id ? `/api/notifications/${notification.id}` : '/api/notifications';
      const method = notification?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/notifications');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/notifications');
  };

  const initialData = notification || {
    type: '',
    notificators: '',
    description: '',
    always: false,
    calendarId: null,
    commandId: null,
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={notification?.id ? null : 'notification-wizard-draft'}
      completeButtonText={notification?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default NotificationFormWizard;
